// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  PolicySignatoryOverrideDTO,
  PolicySignatoryOverrideCreateDTO,
  ProfileOverrideSummaryDTO,
} from '../types/policyOverride';

/**
 * Create a new override for a policy signatory.
 */
export const createOverride = async (
  profileId: string,
  policyId: string,
  signatoryId: string,
  request: PolicySignatoryOverrideCreateDTO
): Promise<PolicySignatoryOverrideDTO> => {
  const response = await api.post<PolicySignatoryOverrideDTO>(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/override`,
    request
  );
  return response.data;
};

/**
 * Update an existing override for a policy signatory.
 */
export const updateOverride = async (
  profileId: string,
  policyId: string,
  signatoryId: string,
  request: PolicySignatoryOverrideCreateDTO
): Promise<PolicySignatoryOverrideDTO> => {
  const response = await api.put<PolicySignatoryOverrideDTO>(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/override`,
    request
  );
  return response.data;
};

/**
 * Delete an override for a policy signatory.
 */
export const deleteOverride = async (
  profileId: string,
  policyId: string,
  signatoryId: string
): Promise<void> => {
  await api.delete(
    `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/override`
  );
};

/**
 * Get the override for a specific policy signatory.
 * Returns null if no override exists.
 */
export const getOverride = async (
  profileId: string,
  policyId: string,
  signatoryId: string
): Promise<PolicySignatoryOverrideDTO | null> => {
  try {
    const response = await api.get<PolicySignatoryOverrideDTO>(
      `/profiles/${profileId}/policies/${policyId}/signatories/${signatoryId}/override`
    );
    return response.data;
  } catch (err) {
    const axiosErr = err as { response?: { status?: number } };
    if (axiosErr.response?.status === 404) return null;
    throw err;
  }
};

/**
 * Get all active overrides for a profile.
 */
export const getProfileOverrides = async (
  profileId: string
): Promise<ProfileOverrideSummaryDTO> => {
  const response = await api.get<ProfileOverrideSummaryDTO>(
    `/profiles/${profileId}/overrides`
  );
  return response.data;
};

export default {
  createOverride,
  updateOverride,
  deleteOverride,
  getOverride,
  getProfileOverrides,
};
